import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import ProductAnimationView from '../components/motion/ProductAnimation';
import { btnPrimary } from '../components/ui';
import { productBySlug, SITE_URL } from '../data/products';
import { organizationSchema, webPageSchema } from '../seo/schema';
import NotFoundPage from './NotFoundPage';

const ProductDetailPage: React.FC = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const product = productBySlug(slug);

  if (!product) return <NotFoundPage />;

  const name = t(`prod.${product.key}.name`);
  const description = t(`prod.${product.key}.seoDescription`);
  const title = t(`prod.${product.key}.seoTitle`);
  const path = `/products/${product.slug}`;

  return (
    <>
      <SEO
        title={title}
        description={description}
        url={`${SITE_URL}${path}`}
        jsonLd={[organizationSchema, webPageSchema(name, description, path)]}
      />
      <article className="mx-auto max-w-[1100px] px-4 py-14 sm:px-6">
        <Link to="/products" className="text-sm text-amber hover:text-mint">
          ← {t('products.back')}
        </Link>
        <p className="mt-6 font-mono text-[0.68rem] uppercase tracking-[0.12em] text-stamp">
          {t(`prod.${product.key}.tag`)} · {t('productPage.illustration')}
        </p>
        <h1 className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl">{name}</h1>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-muted">{t(`prod.${product.key}.does`)}</p>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <section className="rounded-2xl border border-coral/30 bg-graphite p-5">
            <h2 className="font-mono text-[0.68rem] uppercase tracking-wide text-coral">{t('products.problemLabel')}</h2>
            <p className="mt-3 text-paper/90">{t(`prod.${product.key}.problem`)}</p>
          </section>
          <section className="rounded-2xl border border-mint/30 bg-graphite p-5">
            <h2 className="font-mono text-[0.68rem] uppercase tracking-wide text-mint">{t('products.outcomeLabel')}</h2>
            <p className="mt-3 text-paper/90">{t(`prod.${product.key}.outcome`)}</p>
          </section>
        </div>
        <div className="mt-8">
          <h2 className="mb-4 font-display text-2xl">{t('productPage.illustration')}</h2>
          <ProductAnimationView kind={product.animation} />
        </div>
        <Link to="/#talk" className={`${btnPrimary} mt-8`}>
          {t('productPage.talk')}
        </Link>
      </article>
    </>
  );
};

export default ProductDetailPage;
